import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useFeatureFlags } from '@erebus/feature-flags';
import { getRuntimeConfig } from '@erebus/runtime-config';
import { useAuthStore } from '@/stores/authStore';
import { useMigrationStatusStore } from '@/stores/migrationStatusStore';
import { EMPLOYEE_ENTITY } from '@/models/migration';
import type { MenuItem } from 'primevue/menuitem';

export function useNavigation() {
  const router = useRouter();
  const { t } = useI18n();
  const featureFlags = useFeatureFlags();
  const authStore = useAuthStore();
  const migrationStatus = useMigrationStatusStore();

  // The People item needs the employee migration state. Only super admins see
  // the item, so only they load it. A failed load leaves the item hidden.
  watch(
    () => authStore.isSuperAdmin,
    (isSuperAdmin) => {
      if (isSuperAdmin) migrationStatus.load().catch(() => {});
    },
    { immediate: true },
  );

  const generateMenuItems = (): MenuItem[] => {
    // Tenant templates (PRX-2424): super admins only, behind tenant_templates.
    const showTemplates =
      authStore.isSuperAdmin && featureFlags.isEnabled('tenant_templates').value;

    // Tenants is a plain item. When Templates is shown, Tenants and Templates
    // go into one group, the same way IVR Management works.
    const tenantsItem: MenuItem = {
      label: t('tenants.title'),
      icon: 'business_center',
      key: 'tenants',
      command: () => {
        router.push({ name: 'tenants' });
      },
    };
    const tenantsEntry: MenuItem = showTemplates
      ? {
          label: t('navigation.tenantManagement'),
          icon: 'business_center',
          key: 'tenantManagement',
          items: [
            { ...tenantsItem, isChild: true },
            {
              label: t('navigation.tenantTemplates'),
              icon: 'content_copy',
              key: 'tenantTemplates',
              isChild: true,
              command: () => {
                router.push({ name: 'tenantTemplates' });
              },
            },
          ],
        }
      : tenantsItem;

    const items: MenuItem[] = [
      {
        label: t('home.title'),
        icon: 'home',
        key: 'home',
        command: () => {
          router.push({ name: 'home' });
        },
      },
      tenantsEntry,
      {
        label: t('navigation.ivrManagement'),
        icon: 'phone_in_talk',
        key: 'ivrManagement',
        items: [
          {
            label: t('ivrFlows.title'),
            icon: 'call_split',
            key: 'ivrFlows',
            isChild: true,
            command: () => {
              router.push({ name: 'ivrFlows' });
            },
          },
          {
            label: t('ivrNumbers.title'),
            icon: 'dialpad',
            key: 'ivrNumbers',
            isChild: true,
            command: () => {
              router.push({ name: 'ivrNumbers' });
            },
          },
          {
            label: t('ivrMessaging.title'),
            icon: 'message',
            key: 'ivrMessaging',
            isChild: true,
            command: () => {
              router.push({ name: 'ivrMessaging' });
            },
          },
        ],
      },
      {
        label: t('featureFlags.title'),
        icon: 'flag',
        key: 'featureFlags',
        command: () => {
          router.push({ name: 'featureFlags' });
        },
      },
      {
        label: t('modules.tiers.title'),
        icon: 'extension',
        key: 'moduleManagement',
        command: () => {
          router.push({ name: 'moduleManagement' });
        },
      },
    ];

    // Add migration state console if migration_console_v1 feature flag is enabled
    if (featureFlags.hasMigrationConsoleV1.value) {
      items.push({
        label: t('migration.title'),
        icon: 'sync',
        key: 'migrationState',
        command: () => {
          router.push({ name: 'migrationState' });
        },
      });
    }

    // Add the IVR ops reports for super admins when ivr_ops_dashboard is enabled
    if (featureFlags.hasIvrOpsDashboard.value && authStore.isSuperAdmin) {
      items.push({
        label: t('reports.title'),
        icon: 'monitoring',
        key: 'reports',
        command: () => {
          router.push({ name: 'reports' });
        },
      });
    }

    // Users area (PRX-2160): find a user and reset their MFA. Super admins
    // only, behind auth_admin_mfa_reset, and only with cookie auth, which the
    // backend's permission check needs.
    if (
      authStore.isSuperAdmin &&
      featureFlags.hasAuthAdminMfaReset.value &&
      getRuntimeConfig().useCookieAuth
    ) {
      items.push({
        label: t('users.title'),
        icon: 'group',
        key: 'users',
        command: () => {
          router.push({ name: 'users' });
        },
      });
    }

    // People directory (PRX-2519): super admins only, once the employee
    // migration state says Erebus owns the reads.
    if (authStore.isSuperAdmin && migrationStatus.readsFromErebus(EMPLOYEE_ENTITY)) {
      items.push({
        label: t('people.title'),
        icon: 'badge',
        key: 'people',
        command: () => {
          router.push({ name: 'people' });
        },
      });
    }

    // Add scheduling menu if scheduling_v1 feature flag is enabled
    if (featureFlags.hasSchedulingV1.value) {
      // Insert after IVR Management (index 2)
      items.splice(3, 0, {
        label: t('scheduling.title'),
        icon: 'calendar_month',
        key: 'scheduling',
        items: [
          {
            label: t('scheduling.schedule.title'),
            icon: 'calendar_today',
            key: 'schedule',
            isChild: true,
            command: () => {
              router.push({ name: 'schedule' });
            },
          },
          {
            label: t('scheduling.groups.title'),
            icon: 'groups',
            key: 'scheduleGroups',
            isChild: true,
            command: () => {
              router.push({ name: 'scheduleGroups' });
            },
          },
          {
            label: t('scheduling.lines.title'),
            icon: 'timeline',
            key: 'scheduleLines',
            isChild: true,
            command: () => {
              router.push({ name: 'scheduleLines' });
            },
          },
        ],
      });
    }

    return items;
  };

  return {
    generateMenuItems,
  };
}
